/**
 * InputWithCta — small inline form with a floating-label input + submit button.
 *
 * Used by the Builder EmailSignup component in the footer. Uses a fetcher form
 * (no full-page navigation) with client-side Zod validation + server-side
 * validation in the resource route. Keeps the composable's floating-label
 * animation, checkmark success state, and `whiteMode` prop.
 *
 * Submission posts to a caller-supplied resource route (the email-signup
 * component points to `/resources/email-signup`). The route validates and
 * returns `{ ok: true }` on success.
 */
import { Checkmark } from "hec/assets/icons";
import { type FormEvent, useRef, useState } from "react";
import { useFetcher } from "react-router";
import { z } from "zod";

import { Input } from "~/hec/components/ui/input";
import { cn } from "~/lib/utils";

interface InputWithCtaProps {
  whiteMode?: boolean;
  inputType?: "text" | "email";
  inputName: string;
  label: string;
  ctaText: string;
  className?: string;
  isRequired?: boolean;
  submitMsg?: string;
  errorMsg: string;
  action: string;
  placement?: string;
  /**
   * Fired in the submit handler once client-side validation passes, with the
   * submitted form values. Used by the EmailSignup block to fire Segment / GTM
   * consent events; kept generic so this component carries no analytics
   * coupling. (Client-valid, not server-confirmed — the email-signup route
   * applies the same schema, so a client-valid submit always validates server
   * side.)
   */
  onValidSubmit?: (values: Record<string, string>) => void;
  /** Fired once per mount on the first focus of the input — form-start
   *  analytics hook (SFCC-11889 C5). Generic callback, same no-analytics-
   *  coupling contract as `onValidSubmit`. */
  onFirstFocus?: () => void;
  /** Fired on every submit attempt, BEFORE client validation — form-submit
   *  analytics hook mirroring GTM's DOM Form Submit semantics. */
  onSubmitAttempt?: () => void;
  "data-testid"?: string;
}

export function InputWithCta({
  whiteMode = false,
  inputType = "text",
  inputName,
  label,
  ctaText,
  className,
  isRequired = false,
  submitMsg,
  errorMsg,
  action,
  placement,
  onValidSubmit,
  onFirstFocus,
  onSubmitAttempt,
  "data-testid": dataTestId,
}: InputWithCtaProps) {
  const fetcher = useFetcher<{ ok?: boolean }>();
  const hasInteracted = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  const isFloated = focused || hasValue;
  const isSubmitting = fetcher.state !== "idle";
  const isSubmitted = fetcher.data?.ok === true && !isSubmitting;

  // Build Zod schema from props (matches composable's dynamic schema pattern)
  const buildSchema = () => {
    let field = isRequired
      ? z
          .string({ required_error: "This field is required" })
          .min(1, "This field is required")
      : z.string();
    if (inputType === "email") {
      field = field.email({ message: errorMsg });
    }
    return z.object({ [inputName]: field });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    onSubmitAttempt?.();
    const formData = new FormData(e.currentTarget);
    const result = buildSchema().safeParse(
      Object.fromEntries(formData.entries()),
    );

    if (!result.success) {
      e.preventDefault();
      const firstError = result.error.errors[0]?.message ?? errorMsg;
      setClientError(firstError);
      return;
    }

    // Validation passed — clear any client error, let the fetcher submit, and
    // notify the caller (e.g. to fire analytics) with the submitted values.
    setClientError(null);
    onValidSubmit?.(
      Object.fromEntries(formData.entries()) as Record<string, string>,
    );
  };

  // Clear error + success when user types
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasValue(!!e.target.value);
    if (clientError) setClientError(null);
  };

  return (
    <fetcher.Form
      ref={formRef}
      method="post"
      action={action}
      onSubmit={handleSubmit}
      className={cn("flex h-[5.0625rem] w-full items-start", className)}
      data-testid={dataTestId ?? "input-with-cta-Form"}
    >
      {/* Floating-label input */}
      <div className="relative flex grow flex-col space-y-0">
        <label
          htmlFor={inputName}
          className={cn(
            "eyebrow-sm pointer-events-none absolute top-[0.9375rem] z-[1] px-2.5 transition-all duration-150",
            isFloated && "eyebrow-sm top-2",
            !isFloated && "eyebrow-common text-xs leading-5 tracking-wide",
            whiteMode ? "text-white" : "text-foreground",
          )}
        >
          {label}
        </label>
        {isSubmitted && (
          <Checkmark className="absolute right-2 top-1/2 h-4 w-4 text-active" />
        )}
        <Input
          id={inputName}
          name={inputName}
          type={inputType}
          autoComplete={inputType === "email" ? "email" : "off"}
          className={cn(whiteMode && "border-white text-white")}
          onFocus={() => {
            setFocused(true);
            if (!hasInteracted.current) {
              hasInteracted.current = true;
              onFirstFocus?.();
            }
          }}
          onBlur={e => {
            setFocused(false);
            setHasValue(!!e.target.value);
          }}
          onChange={handleChange}
        />
        {clientError && (
          <p className="px-0 text-left text-sm text-destructive">
            {clientError}
          </p>
        )}
      </div>

      {/* Hidden placement field for analytics */}
      {placement !== undefined && (
        <input type="hidden" name="placement" value={placement} />
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className={cn(
          "flex h-[3.1875rem] min-w-[6.75rem] items-center justify-center rounded-md rounded-b-none border border-solid border-btn-secondary-text px-[0.6875rem] pb-4 pt-4 font-grotesque text-base font-normal text-btn-secondary-text lg:min-w-[9.1875rem]",
          whiteMode && "border-white text-white",
          isSubmitting && "opacity-60",
        )}
      >
        {isSubmitted && submitMsg ? submitMsg : ctaText}
      </button>
    </fetcher.Form>
  );
}
